import { CubePresenter, FlipIntent, Logger, NOOP_VOID, ScopeSlot } from 'src/3rds/cube'
import { MainPresenter } from 'src/main/Main.presenter'
import { AppStorage } from 'src/utils'
import { ServiceError } from 'src/utils/ServiceError'
import { DashboardsKeys } from './Dashboards.keys'
import { Atalho, atalhoDoPeriodo, hojeEmSaoPaulo, Periodo, periodoDoAtalho } from './Dashboards.periodo'
import { PERSPECTIVAS, Perspectiva, perspectivasDoPapel } from './Dashboards.perspectivas'
// O gerador de PDF (jsPDF e o desenho dos gráficos) só é carregado no clique em "Baixar PDF".
import type { Contexto } from './Dashboards.pdf'
import { etiquetasDosFiltros } from './Consultas.graficos'
import {
    chaveDoCaminho,
    CONSULTAS_POR_PAGINA,
    DashboardsScope,
    DLQ_POR_PAGINA,
    FiltrosConsultas,
    OrdemParceiros,
    Origem,
    PARCEIROS_POR_PAGINA,
    RESERVAS_POR_PAGINA,
    ROTULO_SITUACAO,
    SituacaoParceiro
} from './Dashboards.scopes'
import { DashboardsService } from './Dashboards.service'

const LOG = Logger.get('DashboardsPresenter')
const service = DashboardsService.singleton()
const appStorage = AppStorage.singleton()

// Espera o operador parar de digitar antes de consultar o servidor.
const DEBOUNCE_MS = 300

/** Confere o período antes de ir ao servidor; devolve a mensagem para a tela ou undefined. */
export function problemaDoPeriodo(periodo: Periodo, hoje = hojeEmSaoPaulo()): string | undefined {
    if (!periodo.de || !periodo.ate) return 'Informe as duas datas.'
    if (periodo.de > periodo.ate) return 'O início vem depois do fim.'
    if (periodo.de > hoje) return 'O período começa no futuro.'
    return undefined
}

export class DashboardsPresenter extends CubePresenter<MainPresenter, DashboardsScope> {
    private parentSlot: ScopeSlot = NOOP_VOID
    // Só a resposta da última consulta vale: o operador pode trocar o período antes da anterior voltar.
    private consulta = 0
    private consultaDlq = 0
    private consultaVencendo = 0
    private consultaRanking = 0
    private consultaDetalhe = 0
    private consultaSugestao = 0
    // Muda a cada nova carga do dashboard de consultas: os filhos da árvore que chegarem depois são descartados.
    private geracaoConsultas = 0
    private opcoesDoPeriodo = ''
    private buscaTimer?: ReturnType<typeof setTimeout>
    private sugestaoTimer?: ReturnType<typeof setTimeout>

    constructor(app: MainPresenter) {
        super(app, new DashboardsScope())
    }

    public override release() {
        if (this.buscaTimer) clearTimeout(this.buscaTimer)
        if (this.sugestaoTimer) clearTimeout(this.sugestaoTimer)
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        const keys = new DashboardsKeys(this.app, intent)

        if (initialization) {
            const perspectivas = perspectivasDoPapel(appStorage.userRole)
            if (perspectivas.length === 0) {
                this.app.alert('warning', 'Acesso restrito', 'Os dashboards não estão liberados para o seu perfil.')
                keys.allow = false
                return keys.allow
            }
            this.scope.perspectivas = perspectivas
            this.parentSlot = keys.parentSlot
            this.#initialize()
        }

        const aba = this.scope.perspectivas.some((p) => p.id === keys.aba) ? keys.aba! : this.scope.perspectivas[0].id
        const periodo = keys.de && keys.ate ? { de: keys.de, ate: keys.ate } : periodoDoAtalho('30')
        const mudou =
            initialization ||
            aba !== this.scope.aba ||
            periodo.de !== this.scope.de ||
            periodo.ate !== this.scope.ate ||
            keys.origem !== this.scope.origem

        this.scope.aba = aba
        this.scope.origem = keys.origem
        this.scope.de = periodo.de
        this.scope.ate = periodo.ate
        this.scope.atalho = atalhoDoPeriodo(periodo)

        if (mudou) {
            await this.#carregar()
        }

        this.parentSlot(this.scope)

        return keys.allow
    }

    public override publishParameters(intent: FlipIntent): void {
        const keys = new DashboardsKeys(this.app, intent)
        keys.aba = this.scope.aba
        keys.origem = this.scope.origem
        // O padrão (30 dias até hoje) não vai para a URL: assim o link aberto amanhã mostra os 30 dias de amanhã.
        const padrao = this.scope.atalho === '30'
        keys.de = padrao ? undefined : this.scope.de
        keys.ate = padrao ? undefined : this.scope.ate
    }

    #initialize() {
        LOG.debug('initialize')

        this.scope.onBaixarPdf = this.action(this.#handleBaixarPdf, this)
        this.scope.onAbaChange = this.action(this.#handleAbaChange, this)
        this.scope.onAtalhoChange = this.action(this.#handleAtalhoChange, this)
        this.scope.onDeChange = this.action(this.#handleDeChange, this)
        this.scope.onAteChange = this.action(this.#handleAteChange, this)
        this.scope.onAtualizar = this.action(this.#handleAtualizar, this)

        this.scope.onBuscaParceiroChange = this.action(this.#handleBuscaParceiroChange, this)
        this.scope.onSituacaoParceiroChange = this.action(this.#handleSituacaoParceiroChange, this)
        this.scope.onOrdemParceirosChange = this.action(this.#handleOrdemParceirosChange, this)
        this.scope.onRankingPaginaChange = this.action(this.#handleRankingPaginaChange, this)

        this.scope.onOrigemChange = this.action(this.#handleOrigemChange, this)
        this.scope.onVencendoDiasChange = this.action(this.#handleVencendoDiasChange, this)
        this.scope.onVencendoPaginaChange = this.action(this.#handleVencendoPaginaChange, this)

        this.scope.onDlqCanalChange = this.action(this.#handleDlqCanalChange, this)
        this.scope.onDlqPaginaChange = this.action(this.#handleDlqPaginaChange, this)

        this.scope.onFiltrosConsultasChange = this.action(this.#handleFiltrosConsultasChange, this)
        this.scope.onAlternarParceiro = this.action(this.#handleAlternarParceiro, this)
        this.scope.onDetalhePaginaChange = this.action(this.#handleDetalhePaginaChange, this)
        this.scope.onBuscarSugestaoParceiro = this.action(this.#handleBuscarSugestaoParceiro, this)
    }

    // :: Abas e período

    async #handleAbaChange(aba: Perspectiva) {
        if (aba === this.scope.aba) return
        this.scope.aba = aba
        this.updateHistory()
        await this.#carregar()
    }

    async #handleAtalhoChange(atalho: Atalho) {
        this.scope.atalho = atalho
        if (atalho === 'livre') return
        const periodo = periodoDoAtalho(atalho)
        this.scope.de = periodo.de
        this.scope.ate = periodo.ate
        this.updateHistory()
        await this.#carregar()
    }

    async #handleDeChange(de: string) {
        this.scope.de = de
        await this.#periodoLivreMudou()
    }

    async #handleAteChange(ate: string) {
        this.scope.ate = ate
        await this.#periodoLivreMudou()
    }

    async #periodoLivreMudou() {
        this.scope.atalho = 'livre'
        this.scope.erroPeriodo = problemaDoPeriodo({ de: this.scope.de, ate: this.scope.ate })
        if (this.scope.erroPeriodo) return
        this.updateHistory()
        await this.#carregar()
    }

    async #handleAtualizar() {
        await this.#carregar()
    }

    // :: Carga

    async #carregar() {
        const periodo = { de: this.scope.de, ate: this.scope.ate }
        this.scope.erroPeriodo = problemaDoPeriodo(periodo)
        if (this.scope.erroPeriodo) return

        if (this.scope.aba === 'operacao') {
            this.scope.dlqPagina = 0
            await Promise.all([
                this.#carregarPainel(
                    'Carregando o dashboard de operação',
                    () => service.operacao(periodo),
                    (operacao) => (this.scope.operacao = operacao)
                ),
                this.#carregarDlq()
            ])
        } else if (this.scope.aba === 'parceiros') {
            this.scope.rankingPagina = 0
            await Promise.all([
                this.#carregarPainel(
                    'Carregando o dashboard de parceiros',
                    () => service.parceiros(periodo),
                    (parceiros) => (this.scope.parceiros = parceiros)
                ),
                this.#carregarRanking()
            ])
        } else if (this.scope.aba === 'consultas') {
            await Promise.all([this.#carregarConsultas(), this.#carregarOpcoes()])
        } else if (this.scope.aba === 'negocios') {
            this.scope.vencendoPagina = 0
            await Promise.all([
                this.#carregarPainel(
                    'Carregando o dashboard de negócios',
                    () => service.negocios(periodo, this.scope.origem),
                    (negocios) => (this.scope.negocios = negocios)
                ),
                this.#carregarVencendo()
            ])
        }
    }

    /** Carrega o painel da aba; só a resposta da última consulta vale. */
    async #carregarPainel<T>(oQue: string, buscar: () => Promise<T>, aplicar: (resposta: T) => void) {
        const consulta = ++this.consulta
        this.scope.carregando = true
        try {
            const resposta = await buscar()
            if (consulta !== this.consulta) return
            aplicar(resposta)
        } catch (error) {
            if (consulta !== this.consulta) return
            if (error instanceof ServiceError && error.statusCode === 400) {
                this.scope.erroPeriodo = error.message
            } else {
                this.unexpected(oQue, error)
            }
        } finally {
            if (consulta === this.consulta) this.scope.carregando = false
        }
    }

    // :: PDF

    /** Filtros da aba em texto, para o cabeçalho do PDF. */
    #filtrosEmTexto(): string[] {
        const { scope } = this
        if (scope.aba === 'negocios') {
            return [scope.origem === 'lia' ? 'Só os negócios criados pela LIA' : 'Todos os negócios']
        }
        if (scope.aba === 'parceiros') {
            const filtros = [`Situação: ${ROTULO_SITUACAO[scope.situacaoParceiro]}`]
            if (scope.buscaParceiro.trim()) filtros.push(`Busca: “${scope.buscaParceiro.trim()}”`)
            return filtros
        }
        if (scope.aba === 'consultas') {
            return etiquetasDosFiltros(scope.filtrosConsultas, scope.nomesParceiros).map((e) => e.rotulo)
        }
        if (scope.aba === 'operacao' && scope.dlqCanal) {
            return [`Mensagens na DLQ só do canal ${scope.dlqCanal}`]
        }
        return []
    }

    async #handleBaixarPdf() {
        const { scope } = this
        const periodo = { de: scope.de, ate: scope.ate }
        const definicao = PERSPECTIVAS.find((p) => p.id === scope.aba)
        if (!definicao || scope.gerandoPdf) return

        scope.gerandoPdf = true
        try {
            const contexto: Contexto = {
                titulo: definicao.titulo,
                pergunta: definicao.pergunta,
                periodo,
                filtros: this.#filtrosEmTexto(),
                geradoPor: appStorage.userName || 'portal',
                geradoEm: new Date()
            }
            const pdf = await import('./Dashboards.pdf')
            let doc
            if (scope.aba === 'operacao' && scope.operacao) {
                const dlq = await service.dlq(periodo, scope.dlqCanal, 0, pdf.LINHAS_DAS_LISTAS)
                doc = pdf.relatorioOperacao(contexto, scope.operacao, dlq)
            } else if (scope.aba === 'negocios' && scope.negocios) {
                const vencendo = await service.reservasVencendo(
                    scope.origem,
                    scope.vencendoDias,
                    0,
                    pdf.LINHAS_DAS_LISTAS
                )
                doc = pdf.relatorioNegocios(contexto, scope.negocios, vencendo, scope.vencendoDias)
            } else if (scope.aba === 'parceiros' && scope.parceiros) {
                const ranking = await service.rankingParceiros(
                    periodo,
                    scope.buscaParceiro.trim(),
                    scope.situacaoParceiro,
                    scope.ordemParceiros,
                    0,
                    pdf.LINHAS_DAS_LISTAS
                )
                doc = pdf.relatorioParceiros(contexto, scope.parceiros, ranking)
            } else if (scope.aba === 'consultas' && scope.consultas) {
                const detalhe = await service.detalheConsultas(
                    periodo,
                    scope.filtrosConsultas,
                    0,
                    pdf.LINHAS_DAS_LISTAS
                )
                doc = pdf.relatorioConsultas(contexto, scope.consultas, detalhe)
            }
            doc?.save(pdf.nomeDoArquivo(scope.aba, periodo))
        } catch (error) {
            this.unexpected('Gerando o PDF do dashboard', error)
        } finally {
            scope.gerandoPdf = false
        }
    }

    // :: Consultas de lead

    /** O painel, a árvore de parceiros fechada e a primeira página do detalhe, com os filtros da tela. */
    async #carregarConsultas() {
        const periodo = { de: this.scope.de, ate: this.scope.ate }
        const filtros = this.scope.filtrosConsultas
        this.geracaoConsultas++
        this.scope.parceirosAbertos = []
        this.scope.filhosParceiros = {}
        this.scope.carregandoParceiro = ''
        this.scope.detalhePagina = 0
        await Promise.all([
            this.#carregarPainel(
                'Carregando o dashboard de consultas',
                () => service.consultas(periodo, filtros),
                (consultas) => (this.scope.consultas = consultas)
            ),
            this.#carregarDetalhe()
        ])
    }

    /** Valores para os filtros; os desfechos dependem do período. */
    async #carregarOpcoes() {
        const periodo = { de: this.scope.de, ate: this.scope.ate }
        const chave = `${periodo.de}|${periodo.ate}`
        if (chave === this.opcoesDoPeriodo) return
        this.opcoesDoPeriodo = chave
        try {
            const opcoes = await service.opcoesConsultas(periodo)
            if (chave === this.opcoesDoPeriodo) this.scope.opcoesConsultas = opcoes
        } catch (error) {
            if (chave === this.opcoesDoPeriodo) this.opcoesDoPeriodo = ''
            if (!(error instanceof ServiceError && error.statusCode === 400)) {
                this.unexpected('Carregando os valores dos filtros', error)
            }
        }
    }

    async #handleFiltrosConsultasChange({
        filtros,
        nomes
    }: {
        filtros: FiltrosConsultas
        nomes?: Record<string, string>
    }) {
        if (nomes) this.scope.nomesParceiros = { ...this.scope.nomesParceiros, ...nomes }
        this.scope.filtrosConsultas = filtros
        await this.#carregarConsultas()
    }

    /** Abre ou fecha um nó da árvore de parceiros; os filhos vêm do servidor na primeira vez. */
    async #handleAlternarParceiro(caminho: string[]) {
        const chave = chaveDoCaminho(caminho)
        const { scope } = this
        if (scope.parceirosAbertos.includes(chave)) {
            scope.parceirosAbertos = scope.parceirosAbertos.filter((c) => c !== chave && !c.startsWith(`${chave}/`))
            return
        }
        if (!scope.filhosParceiros[chave]) {
            const geracao = this.geracaoConsultas
            scope.carregandoParceiro = chave
            try {
                const filhos = await service.filhosParceiro(
                    { de: scope.de, ate: scope.ate },
                    scope.filtrosConsultas,
                    caminho
                )
                if (geracao !== this.geracaoConsultas) return
                scope.filhosParceiros = { ...scope.filhosParceiros, [chave]: filhos }
            } catch (error) {
                if (geracao === this.geracaoConsultas) this.unexpected('Abrindo a rede do parceiro', error)
                return
            } finally {
                if (scope.carregandoParceiro === chave) scope.carregandoParceiro = ''
            }
        }
        scope.parceirosAbertos = [...scope.parceirosAbertos, chave]
    }

    async #handleDetalhePaginaChange(pagina: number) {
        this.scope.detalhePagina = pagina
        await this.#carregarDetalhe()
    }

    async #carregarDetalhe() {
        const consulta = ++this.consultaDetalhe
        this.scope.carregandoDetalhe = true
        try {
            const pagina = await service.detalheConsultas(
                { de: this.scope.de, ate: this.scope.ate },
                this.scope.filtrosConsultas,
                this.scope.detalhePagina * CONSULTAS_POR_PAGINA,
                CONSULTAS_POR_PAGINA
            )
            if (consulta !== this.consultaDetalhe) return
            this.scope.detalheTotal = pagina.total
            this.scope.detalhe = pagina.itens
        } catch (error) {
            if (consulta !== this.consultaDetalhe) return
            // Período ou filtro inválido já aparece pelo painel; aqui só o inesperado.
            if (!(error instanceof ServiceError && error.statusCode === 400)) {
                this.unexpected('Carregando as consultas', error)
            }
        } finally {
            if (consulta === this.consultaDetalhe) this.scope.carregandoDetalhe = false
        }
    }

    async #handleBuscarSugestaoParceiro(texto: string) {
        if (this.sugestaoTimer) clearTimeout(this.sugestaoTimer)
        const busca = texto.trim()
        if (busca.length < 2) {
            this.consultaSugestao++
            this.scope.sugestoesParceiro = []
            return
        }
        this.sugestaoTimer = setTimeout(() => {
            this.sugestaoTimer = undefined
            const consulta = ++this.consultaSugestao
            service
                .buscarParceiros(busca)
                .then((encontrados) => {
                    if (consulta === this.consultaSugestao) this.scope.sugestoesParceiro = encontrados
                })
                .catch((error) => LOG.error('Buscando parceiros para o filtro', error))
        }, DEBOUNCE_MS)
    }

    // :: Parceiros

    async #handleBuscaParceiroChange(texto: string) {
        this.scope.buscaParceiro = texto
        if (this.buscaTimer) clearTimeout(this.buscaTimer)
        this.buscaTimer = setTimeout(() => {
            this.buscaTimer = undefined
            this.scope.rankingPagina = 0
            this.#carregarRanking().catch((error) => LOG.error('Buscando parceiros', error))
        }, DEBOUNCE_MS)
    }

    async #handleSituacaoParceiroChange(situacao: SituacaoParceiro) {
        this.scope.situacaoParceiro = situacao
        this.scope.rankingPagina = 0
        await this.#carregarRanking()
    }

    async #handleOrdemParceirosChange(ordem: OrdemParceiros) {
        this.scope.ordemParceiros = ordem
        this.scope.rankingPagina = 0
        await this.#carregarRanking()
    }

    async #handleRankingPaginaChange(pagina: number) {
        this.scope.rankingPagina = pagina
        await this.#carregarRanking()
    }

    async #carregarRanking() {
        const consulta = ++this.consultaRanking
        this.scope.carregandoRanking = true
        try {
            const pagina = await service.rankingParceiros(
                { de: this.scope.de, ate: this.scope.ate },
                this.scope.buscaParceiro.trim(),
                this.scope.situacaoParceiro,
                this.scope.ordemParceiros,
                this.scope.rankingPagina * PARCEIROS_POR_PAGINA,
                PARCEIROS_POR_PAGINA
            )
            if (consulta !== this.consultaRanking) return
            this.scope.rankingTotal = pagina.total
            this.scope.ranking = pagina.itens
        } catch (error) {
            if (consulta !== this.consultaRanking) return
            if (!(error instanceof ServiceError && error.statusCode === 400)) {
                this.unexpected('Carregando o ranking de parceiros', error)
            }
        } finally {
            if (consulta === this.consultaRanking) this.scope.carregandoRanking = false
        }
    }

    // :: Negócios

    async #handleOrigemChange(origem: Origem) {
        if (origem === this.scope.origem) return
        this.scope.origem = origem
        this.updateHistory()
        await this.#carregar()
    }

    async #handleVencendoDiasChange(dias: 7 | 15) {
        this.scope.vencendoDias = dias
        this.scope.vencendoPagina = 0
        await this.#carregarVencendo()
    }

    async #handleVencendoPaginaChange(pagina: number) {
        this.scope.vencendoPagina = pagina
        await this.#carregarVencendo()
    }

    async #carregarVencendo() {
        const consulta = ++this.consultaVencendo
        this.scope.carregandoVencendo = true
        try {
            const pagina = await service.reservasVencendo(
                this.scope.origem,
                this.scope.vencendoDias,
                this.scope.vencendoPagina * RESERVAS_POR_PAGINA,
                RESERVAS_POR_PAGINA
            )
            if (consulta !== this.consultaVencendo) return
            this.scope.vencendoTotal = pagina.total
            this.scope.vencendo = pagina.itens
        } catch (error) {
            if (consulta !== this.consultaVencendo) return
            this.unexpected('Carregando as reservas que vencem', error)
        } finally {
            if (consulta === this.consultaVencendo) this.scope.carregandoVencendo = false
        }
    }

    // :: DLQ

    async #handleDlqCanalChange(canal: string) {
        this.scope.dlqCanal = canal
        this.scope.dlqPagina = 0
        await this.#carregarDlq()
    }

    async #handleDlqPaginaChange(pagina: number) {
        this.scope.dlqPagina = pagina
        await this.#carregarDlq()
    }

    async #carregarDlq() {
        const consulta = ++this.consultaDlq
        this.scope.carregandoDlq = true
        try {
            const pagina = await service.dlq(
                { de: this.scope.de, ate: this.scope.ate },
                this.scope.dlqCanal,
                this.scope.dlqPagina * DLQ_POR_PAGINA,
                DLQ_POR_PAGINA
            )
            if (consulta !== this.consultaDlq) return
            this.scope.dlqTotal = pagina.total
            this.scope.dlq = pagina.itens
        } catch (error) {
            if (consulta !== this.consultaDlq) return
            // Período inválido já aparece pelo dashboard; aqui só o inesperado.
            if (!(error instanceof ServiceError && error.statusCode === 400)) {
                this.unexpected('Carregando as mensagens da DLQ', error)
            }
        } finally {
            if (consulta === this.consultaDlq) this.scope.carregandoDlq = false
        }
    }
}
